import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from './api';
import { BalanceCompte, BalanceTotaux } from '../models/models';

const getDefaultDateRange = () => {
  const today = new Date();
  const oneMonthAgo = new Date(today);
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { startDate: fmt(oneMonthAgo), endDate: fmt(today) };
};

export interface Compte {
  id: number;
  numero_doc: string;
  intitule: string;
  solde: number;
  // Add other fields as needed
}

export interface CompteFilter {
  tiers_id:number | null;
  caisse_id:number | null;
  startDate?: string | null;
  endDate?: string | null;
  limit: number;
  offset: number;
}

export interface BalanceFilter {
  type_tiers: 'client' | 'fournisseur' | 'employe';
  date_debut: string | null;   // null = depuis le début
  date_fin:   string | null;   // null = jusqu'à aujourd'hui
  categorie_id: number | null;  // null = toutes catégories (CATEGORIE_CLIENT/FOURNISSEUR)
  limit:  number;
  offset: number;
}

export type CompteTab = 'detail' | 'balance';

interface CompteState {
  comptes: Compte[];
  total: number;
  totalPages: number;
  currentPage: number;
  // Solde reporté (avant date_debut) et solde final (après date_fin) — visibles même si 0 transactions
  solde_depart: number;
  solde_final: number;
  // Balance par tiers
  balanceData: BalanceCompte[];
  totalBalance: number;
  totalPagesBalance: number;
  currentPageBalance: number;
  balanceTotaux: BalanceTotaux;   // totaux généraux (tous tiers filtrés)
  balanceFilters: BalanceFilter;
  // Commun
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  selectedCompte: Compte | null;
  compteFilters: CompteFilter;
  // Onglet actif de la page Comptes — dans le store pour survivre à un aller-retour
  // vers un document ouvert depuis une écriture.
  activeTab: CompteTab;
}

const initialBalanceFilters: BalanceFilter = {
  type_tiers: 'client',
  date_debut: null,         // période par défaut = tout
  date_fin:   null,
  categorie_id: null,
  limit:  10,
  offset: 0,
};

const initialState: CompteState = {
  comptes: [],
  total: 0,
  totalPages: 0,
  currentPage: 0,
  solde_depart: 0,
  solde_final: 0,
  balanceData: [],
  totalBalance: 0,
  totalPagesBalance: 0,
  currentPageBalance: 0,
  balanceTotaux: { solde_anterieur: 0, total_facture: 0, total_reglement: 0, solde_final: 0 },
  balanceFilters: initialBalanceFilters,
  loading: false,
  error: null,
  successMessage: null,
  selectedCompte: null,
  compteFilters: {
    tiers_id: null,
    caisse_id: null,
    ...getDefaultDateRange(),
    limit: 5,
    offset: 0,
  },
  activeTab: 'detail',
};

// Fetch comptes
export const fetchComptes = createAsyncThunk<
  { data: Compte[]; total: number; totalPages: number; currentPage: number; solde_depart: number; solde_final: number },
  CompteFilter,
  { rejectValue: string }
>(
  'compte/fetchComptes',
  async ({tiers_id,startDate,endDate,limit,offset}, thunkAPI) => {
    try {
      const response = await api.get('/comptes', {
        params: {tiers_id,startDate,endDate,limit,offset},
      });
   
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to fetch comptes');
    }
  }
);


// Fetch balance par tiers (client / fournisseur)
export const fetchBalance = createAsyncThunk<
  { data: BalanceCompte[]; total: number; totalPages: number; currentPage: number; totaux: BalanceTotaux },
  BalanceFilter,
  { rejectValue: string }
>(
  'compte/fetchBalance',
  async (params, thunkAPI) => {
    try {
      const response = await api.get('/comptes/balance', { params });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to fetch balance');
    }
  }
);

// Create or update a compte
export const createOrUpdateCompte = createAsyncThunk<
  Compte,
  Compte,
  { rejectValue: string }
>(
  'compte/createOrUpdateCompte',
  async (compte, thunkAPI) => {
    try {
      const response = await api.post('/comptes', compte);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to save compte');
    }
  }
);

const compteSlice = createSlice({
  name: 'compte',
  initialState,
  reducers: {
    setCompteFilters: (state, action: PayloadAction<Partial<CompteFilter>>) => {
      state.compteFilters = { ...state.compteFilters, ...action.payload };
    },
    setBalanceFilters: (state, action: PayloadAction<Partial<BalanceFilter>>) => {
      const isPageChange = 'offset' in action.payload;
      // Changer de type de tiers invalide la catégorie sélectionnée
      // (une CATEGORIE_CLIENT n'a pas de sens pour un fournisseur).
      const typeChanged = 'type_tiers' in action.payload
        && action.payload.type_tiers !== state.balanceFilters.type_tiers;
      state.balanceFilters = {
        ...state.balanceFilters,
        ...action.payload,
        ...(typeChanged && { categorie_id: null }),
        ...(!isPageChange && { offset: 0 }),
      };
    },
    clearBalanceData: (state) => {
      state.balanceData = [];
    },
    resetBalanceFilters: (state) => {
      state.balanceFilters = initialBalanceFilters;
    },
    setCompteActiveTab: (state, action: PayloadAction<CompteTab>) => {
      state.activeTab = action.payload;
    },
    setSelectedCompte: (state, action: PayloadAction<Compte | null>) => {
      state.selectedCompte = action.payload;
    },
    clearCompteMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
    clearCompteList: (state) => {
      state.comptes = [];
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    clearCompteFilters: (state) => {
      state.compteFilters = {
        tiers_id: null,
        caisse_id: null,
        ...getDefaultDateRange(),
        limit: 5,
        offset: 0,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchComptes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchComptes.fulfilled, (state, action) => {
        state.loading      = false;
        state.comptes      = action.payload.data;
        state.total        = action.payload.total;
        state.totalPages   = action.payload.totalPages;
        state.currentPage  = action.payload.currentPage;
        state.solde_depart = action.payload.solde_depart ?? 0;
        state.solde_final  = action.payload.solde_final  ?? 0;
      })
      .addCase(fetchComptes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || null;
      })
      .addCase(fetchBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBalance.fulfilled, (state, action) => {
        state.loading            = false;
        state.balanceData        = action.payload.data;
        state.totalBalance       = action.payload.total;
        state.totalPagesBalance  = action.payload.totalPages;
        state.currentPageBalance = action.payload.currentPage;
        state.balanceTotaux      = action.payload.totaux
          ?? { solde_anterieur: 0, total_facture: 0, total_reglement: 0, solde_final: 0 };
      })
      .addCase(fetchBalance.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || null;
      })
      .addCase(createOrUpdateCompte.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createOrUpdateCompte.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = 'Compte saved successfully';
      })
      .addCase(createOrUpdateCompte.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || null;
      });
  },
});

export const {
  setCompteFilters,
  setBalanceFilters,
  clearBalanceData,
  resetBalanceFilters,
  setCompteActiveTab,
  setSelectedCompte,
  clearCompteMessages,
  clearCompteList,
  clearCompteFilters,
  setCurrentPage,
} = compteSlice.actions;

export default compteSlice.reducer;
